import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { BarChart } from '../../components/charts/BarChart.jsx';
import { ChartCard } from '../../components/charts/ChartCard.jsx';
import { DonutChart } from '../../components/charts/DonutChart.jsx';
import { LineChart } from '../../components/charts/LineChart.jsx';
import { useChartPalette } from '../../components/charts/useChartPalette.js';
import { MetricCard } from '../../components/ui/Card.jsx';
import { EmptyState } from '../../components/ui/EmptyState.jsx';
import { Spinner } from '../../components/ui/Spinner.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { financeApi } from '../../services/financeApi.js';
import { formatCurrency } from '../../utils/formatters.js';
import { toAgingSeries, toBreakdownSeries, toStatusSeries, toTrendSeries } from './financeCharts.js';

const MONTH_OPTIONS = [6, 12, 24];

export function FinanceDashboardPage() {
  const { t } = useTranslation();
  const palette = useChartPalette();
  const [months, setMonths] = useState(12);

  const snapshot = useQuery({ queryKey: ['finance', 'dashboard'], queryFn: () => financeApi.dashboard().then((r) => r.items) });
  const trend = useQuery({ queryKey: ['finance', 'timeseries', months], queryFn: () => financeApi.timeseries({ months }).then((r) => r.items) });
  const agingAr = useQuery({ queryKey: ['finance', 'aging', 'AR'], queryFn: () => financeApi.aging({ type: 'AR' }).then((r) => r.items) });
  const breakdown = useQuery({ queryKey: ['finance', 'expense-breakdown'], queryFn: () => financeApi.expenseBreakdown().then((r) => r.items) });
  const status = useQuery({ queryKey: ['finance', 'invoice-status'], queryFn: () => financeApi.invoiceStatus().then((r) => r.items) });

  if (snapshot.isLoading) {
    return (<section className="page"><Spinner /></section>);
  }

  const metrics = snapshot.data?.metrics || {};
  const trendSeries = toTrendSeries(trend.data?.months);
  const agingSeries = toAgingSeries(agingAr.data?.buckets);
  const breakdownSeries = toBreakdownSeries(breakdown.data?.breakdown);
  const statusSeries = toStatusSeries(status.data?.statuses);
  const accent = palette?.accent || '#1d4ed8';
  const expense = palette?.expense || '#dc2626';
  const success = palette?.series?.[1] || '#059669';

  return (
    <section className="page">
      <header className="page-header">
        <div>
          <h1>{t('finance.dashboard.title')}</h1>
          <p className="muted">{t('finance.dashboard.subtitle')}</p>
        </div>
        <div className="period-switch">
          {MONTH_OPTIONS.map((m) => (
            <button
              key={m}
              type="button"
              className={m === months ? 'period-btn active' : 'period-btn'}
              onClick={() => setMonths(m)}
            >
              {t('finance.dashboard.lastMonths', { count: m })}
            </button>
          ))}
        </div>
      </header>

      <div className="card-grid">
        <MetricCard label={t('dashboard.revenue')} value={formatCurrency(metrics.revenue)} />
        <MetricCard label={t('dashboard.expenses')} value={formatCurrency(metrics.expenses)} />
        <MetricCard label={t('dashboard.netProfit')} value={formatCurrency(metrics.netProfit)} trendVariant="success" />
        <MetricCard label={t('dashboard.cash')} value={formatCurrency(metrics.cash)} />
      </div>

      <div className="dashboard-grid">
        <ChartCard className="span-2" title={t('finance.chart.trend')} subtitle={t('finance.chart.trendSub')}>
          {trend.isLoading ? <Spinner /> : trendSeries.labels.length === 0 ? <EmptyState message={t('common.noData')} /> : (
            <LineChart
              labels={trendSeries.labels}
              datasets={[
                { label: t('dashboard.revenue'), data: trendSeries.revenue, color: accent, fill: true },
                { label: t('dashboard.expenses'), data: trendSeries.expenses, color: expense },
                { label: t('dashboard.netProfit'), data: trendSeries.net, color: success },
              ]}
            />
          )}
        </ChartCard>

        <ChartCard title={t('finance.chart.aging')}>
          {agingAr.isLoading ? <Spinner /> : agingSeries.labels.length === 0 ? <EmptyState message={t('common.noData')} /> : (
            <BarChart labels={agingSeries.labels} datasets={[{ label: t('finance.chart.openAr'), data: agingSeries.amounts, color: accent }]} />
          )}
        </ChartCard>

        <ChartCard title={t('finance.chart.expenseBreakdown')}>
          {breakdown.isLoading ? <Spinner /> : breakdownSeries.labels.length === 0 ? <EmptyState message={t('common.noData')} /> : (
            <DonutChart labels={breakdownSeries.labels} values={breakdownSeries.amounts} />
          )}
        </ChartCard>

        <ChartCard className="span-2" title={t('finance.chart.invoiceStatus')}>
          {status.isLoading ? <Spinner /> : statusSeries.labels.length === 0 ? <EmptyState message={t('common.noData')} /> : (
            <BarChart labels={statusSeries.labels} datasets={[{ label: t('finance.chart.invoiceCount'), data: statusSeries.counts, color: accent }]} />
          )}
        </ChartCard>
      </div>
    </section>
  );
}
